import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp, faChevronDown, faTrophy, faCalendarWeek, faBolt,
  faHeartPulse, faPencil, faHandFist, faMedal, faRobot, faBug,
  faShieldAlt, faSync, faChartBar, faBed,
  faSearch, faCrown, faPersonRunning, faPersonBiking, faPersonSwimming,
  faPersonHiking, faDumbbell, faFire, faCalendar, faBoxArchive, faClock,
  faGift, faSun, faMoon, faStar, faHeart, faCouch, faHandshake,
  faBullseye, faBatteryQuarter, faList, faCubes, faLayerGroup,
  faSuitcase, faGlobe, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import '../styles/Footer.css';

interface Props {
  cycleStart?: string;
  cycleEnd?:   string;
}

export default function Footer({ cycleStart, cycleEnd }: Props) {
  const [rulesOpen, setRulesOpen] = useState(false);
  const [impressumOpen, setImpressumOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const formatLocalTime = (utcHours: number, utcMinutes: number): string => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHours, utcMinutes));
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    const minStr = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
    return `${h12}${minStr} ${ampm}`;
  };

  return (
    <footer className="site-footer">
      <div className="footer-toggles">
        <button className="impressum-toggle" onClick={() => setRulesOpen(o => !o)}>
          How to Play <FontAwesomeIcon icon={rulesOpen ? faChevronUp : faChevronDown} />
        </button>
        <button className="impressum-toggle" onClick={() => setImpressumOpen(o => !o)}>
          Legal Notice <FontAwesomeIcon icon={impressumOpen ? faChevronUp : faChevronDown} />
        </button>
        <button className="impressum-toggle" onClick={() => setPrivacyOpen(o => !o)}>
          <FontAwesomeIcon icon={faShieldAlt} /> Privacy Policy <FontAwesomeIcon icon={privacyOpen ? faChevronUp : faChevronDown} />
        </button>
        <button className="impressum-toggle report-bug-btn" onClick={() => {
          if ((window as any).Tally) {
            (window as any).Tally.openPopup('xXglXG', { emoji: { text: '🐛', animation: 'wave' } });
          } else {
            window.location.hash = 'tally-open=MeP6q8&tally-emoji-text=🐛&tally-emoji-animation=wave';
          }
        }}>
          <FontAwesomeIcon icon={faBug} /> Report Bug
        </button>
      </div>

      {rulesOpen && (
        <div className="impressum-content rules-content">
          <h3><FontAwesomeIcon icon={faTrophy} /> How NoWeakWeek Works</h3>

          <h4><FontAwesomeIcon icon={faCalendarWeek} /> Weekly Cycle</h4>
          <p>Each week runs from <strong>Monday at {formatLocalTime(4, 0)}</strong> to <strong>Sunday at {formatLocalTime(18, 0)}</strong>. Results are revealed Sunday evening after the duel is evaluated.</p>
          <ul>
            <li><FontAwesomeIcon icon={faSync} /> <strong>Activity Sync:</strong> Your Strava activities are synced automatically at <strong>{formatLocalTime(4, 0)}</strong> every day.</li>
            <li><FontAwesomeIcon icon={faChartBar} /> <strong>Results:</strong> Duels are evaluated at <strong>{formatLocalTime(18, 0)}</strong> every Sunday – your result will be available right after.</li>
          </ul>

          <h4><FontAwesomeIcon icon={faBolt} /> Scoring – Heart Rate Zones</h4>
          <p>Points are based on time spent in each heart rate zone. Points increase <strong>linearly</strong> within each zone – higher HR = more points. Your zones are based on your personal Strava heart rate zones:</p>
          <table className="rules-table">
            <thead><tr><th>Zone</th><th>% max HR</th><th>Pts/min</th><th>Pts/hour</th></tr></thead>
            <tbody>
              <tr><td>Zone 0</td><td>Below 50%</td><td>0.00</td><td>0</td></tr>
              <tr><td>Zone 1</td><td>50.1–65%</td><td>0.50–0.70</td><td>30–42</td></tr>
              <tr><td>Zone 2</td><td>65.1–80%</td><td>0.90–1.10</td><td>54–66</td></tr>
              <tr><td>Zone 3</td><td>80.1–87.5%</td><td>1.10–1.30</td><td>66–78</td></tr>
              <tr><td>Zone 4</td><td>87.6–95%</td><td>1.30–1.50</td><td>78–90</td></tr>
              <tr><td>Zone 5</td><td>95.1%+</td><td>1.50–1.70</td><td>90–102</td></tr>
            </tbody>
          </table>

          <h4><FontAwesomeIcon icon={faHeartPulse} /> No Heart Rate Monitor?</h4>
          <p>Without HR data you receive <strong>reduced points</strong> – a flat rate of 0.20 pts/min on your moving time (less than Zone 1).</p>
          <p><FontAwesomeIcon icon={faExclamationTriangle} /> Activities shorter than <strong>5 minutes</strong> receive <strong>0 points</strong>.</p>


          <h4><FontAwesomeIcon icon={faMedal} /> Daily Challenges</h4>
          <p>Each day a challenge is assigned. Complete it for a <strong>double points bonus</strong> on your strongest qualifying workout. Only <strong>1 bonus per day</strong> – it automatically goes to your best activity. The bonus is <strong>capped at 150 points</strong> per activity.</p>

          <h4><FontAwesomeIcon icon={faTrophy} /> Personal Records</h4>
          <p><strong>+1 point per Personal Best</strong> + <strong>1 point per Achievement</strong> (max. 25 pts from achievements) – awarded for Strava segment PRs and activity achievements.</p>

          <h4><FontAwesomeIcon icon={faPencil} /> Manual Activities</h4>
          <p>Manually entered activities (without GPS/device) are shown with a <FontAwesomeIcon icon={faPencil} /> badge but receive <strong>0 points</strong>.</p>

          <h4><FontAwesomeIcon icon={faHandFist} /> Duel & Level</h4>
          <p>Each week you're matched against an opponent with a similar <strong>ELO rating</strong>. Whoever scores more points wins the duel.</p>
          <ul>
            <li><strong>Winner:</strong> Level goes up by 1 (max Level 30)</li>
            <li><strong>Loser:</strong> Loss streak increases. At <strong>3 consecutive losses</strong>, level drops by 1 (min Level 1) and the streak resets</li>
            <li><strong>Draw:</strong> no level change</li>
            <li><strong>ELO:</strong> adjusts based on rating difference – beating a stronger opponent is worth more</li>
          </ul>

          <h4><FontAwesomeIcon icon={faMedal} /> Consolation Points</h4>
          <p>If you lose a duel, you receive <strong>20% of your activity points</strong> as consolation. These are added as a bonus activity in the following week's calendar, giving you a head start.</p>
          <ul>
            <li><FontAwesomeIcon icon={faGift} /> Consolation points are shown as a bonus activity in next week's calendar</li>
            <li><FontAwesomeIcon icon={faSync} /> If you win a duel later, all previous consolation is cleared</li>
          </ul>

          <h4><FontAwesomeIcon icon={faSuitcase} /> Badges</h4>
          <p>Earn badges by reaching milestones (weekly points, win streaks, sport variety, and more). Each badge has <strong>3 tiers</strong> – max 1 upgrade per week. Higher tiers grant <strong>more weekly bonus points</strong>.</p>

          <h4><FontAwesomeIcon icon={faRobot} /> Bot Opponents</h4>
          <p>If no real opponent is available you're matched against a bot with a similar ELO rating. Bots don't gain or lose rating.</p>

          <h4><FontAwesomeIcon icon={faBed} /> Items</h4>
          <p>After each duel, both players earn <strong>Items</strong> as a reward. Items give you special abilities – like earning bonus points, resting without penalty, or seeing your opponent's activities. The more combined points in the duel, the more items you can choose from. Hold up to <strong>7 items</strong> and use <strong>1 per day</strong>.</p>
        </div>
      )}

      {impressumOpen && (
        <div className="impressum-content">
          <h3>Legal Notice (Impressum)</h3>
          <p>
            <strong>Vienom OG</strong><br />
            Margaretenstraße 106/1<br />
            1050 Vienna, Austria
          </p>
          <p>
            <strong>Business purpose:</strong> IT services<br />
            <strong>VAT ID:</strong> ATU66342335<br />
            <strong>Company register no.:</strong> FN 359012 i
          </p>
          <p>
            <strong>Contact:</strong><br />
            E-mail: <a href="mailto:office@vienom.com">office@vienom.com</a>
          </p>
          <p className="impressum-note">
            Supervisory authority: Wirtschaftskammer Wien<br />
            Applicable law: Gewerbeordnung (GewO), Austria
          </p>
        </div>
      )}

      {privacyOpen && (
        <div className="impressum-content">
          <h3><FontAwesomeIcon icon={faShieldAlt} /> Privacy Policy</h3>
          <p><em>Effective Date: June 30, 2026</em></p>

          <p>NoWeakWeek ("we", "us", or "our") is a weekly fitness competition app that connects to Strava. This Privacy Policy explains how we collect, use, and protect your data.</p>

          <h4>1. Data We Collect</h4>
          <p>When you sign in with Strava, we collect:</p>
          <ul>
            <li><strong>Profile data:</strong> Name, avatar, athlete ID</li>
            <li><strong>Activity data:</strong> Workout types, durations, distances, heart rate data, elevation gain</li>
            <li><strong>Heart rate zones:</strong> Your personal Strava heart rate zone thresholds</li>
            <li><strong>Calculated data:</strong> Points, ELO rating, match results, level progression</li>
          </ul>

          <h4>2. How We Use Your Data</h4>
          <ul>
            <li>To calculate your training points based on heart rate zones</li>
            <li>To match you with opponents of similar skill level</li>
            <li>To display your profile and match results within the app</li>
            <li>To generate opponent profiles using only your own data</li>
          </ul>

          <h4>3. Data Storage &amp; Security</h4>
          <p>Your data is stored on encrypted MySQL databases hosted in the EU (Austria). We use HTTPS for all data transmission. Access to the database is restricted and monitored.</p>

          <h4>4. Data Sharing</h4>
          <p>We do <strong>not</strong> sell, license, or share your data with any third parties. Your data is only used within the NoWeakWeek application. Your opponent sees only your calculated points and basic profile (name, avatar, level) — never your raw activity data.</p>

          <h4>5. Data Retention</h4>
          <p>We retain your data as long as your account is active. When you delete your account, all personal data, activities, and match history are permanently deleted.</p>

          <h4>6. Your Rights</h4>
          <ul>
            <li><strong>Access:</strong> You can view all your data in the app</li>
            <li><strong>Deletion:</strong> You can delete your account and all data via Settings</li>
            <li><strong>Revoke Strava access:</strong> You can revoke our access anytime in your Strava account settings (strava.com/settings/apps) or via the Delete Account feature</li>
          </ul>

          <h4>7. Strava Data</h4>
          <p>We access your Strava data via the Strava API under Strava's <a href="https://www.strava.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#6ba3ff' }}>Privacy Policy</a> and <a href="https://www.strava.com/legal/api" target="_blank" rel="noopener noreferrer" style={{ color: '#6ba3ff' }}>API Agreement</a>.</p>

          <h4>8. Cookies</h4>
          <p>We use only essential cookies required for authentication (OAuth state tokens). No tracking or analytics cookies are set by our application.</p>

          <h4>9. Changes to This Policy</h4>
          <p>We may update this Privacy Policy from time to time. Changes will be reflected on this page with an updated effective date.</p>

          <h4>10. Contact</h4>
          <p>For privacy-related questions, contact us at: <a href="mailto:office@vienom.com" style={{ color: '#6ba3ff' }}>office@vienom.com</a></p>
        </div>
      )}

      <div className="footer-strava-attribution">
        <a href="https://www.strava.com" target="_blank" rel="noopener noreferrer" className="strava-attribution-link">
          Powered by <strong>Strava</strong>
        </a>
      </div>
    </footer>
  );
}
